import { Avatar } from 'tio-ui/components/utilities';
import Component from '@glimmer/component';
import type { Appearance } from '../../services/appearance.ts';
import { transformToColor } from '../../utils/style/transform-to-color.ts';
import { concat, hash } from '@ember/helper';

interface WhosViewingComponentSignature {
  Args: {
    appearances: Appearance[];
  };
  Element: HTMLDivElement;
}

// Define a set of colors for avatar backgrounds
const avatarColors = [
  'bg-[#BC1317]', // Red
  'bg-[#39C2C9]', // Purple Alt (cyan-like)
  'bg-[#FFA71A]', // Orange Alt
  'bg-[#06C46B]', // Green
  'bg-[#6E44C1]', // Purple
  'bg-[#FFCE00]', // Yellow
  'bg-[#108aa7]', // Blue
  'bg-[#F866B9]', // Pink
  'bg-[#843471]', // Dark Purple
  'bg-[#32fd9e]', // Bright Green
  'bg-[#FF793F]', // Orange
  'bg-[#998CE3]', // Light Purple
  'bg-[#3fb6ab]', // Teal
  'bg-[#6AEDC7]', // Light Green
];

const firstName = (name: string): string => {
  const nameParts = name.split(' ').filter(Boolean); // Split the name into parts and remove any empty strings
  return nameParts[0] || ''; // Return the first part or an empty string if no parts exist
};

const lastName = (name: string): string => {
  const nameParts = name.split(' ').filter(Boolean); // Split the name into parts and remove any empty strings
  return nameParts[nameParts.length - 1] || ''; // Return the last part or an empty string if no parts exist
};

const getAvatarColor = (name: string): string => {
  return transformToColor(name, avatarColors);
};

export default class WhosViewingComponent extends Component<WhosViewingComponentSignature> {
  get appearancesArray() {
    if (!this.args.appearances) return [];
    return Object.values(this.args.appearances);
  }

  <template>
    <div
      class="inline-flex h-8 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full font-normal"
      ...attributes
    >
      {{#each this.appearancesArray as |appearance index|}}
        <div
          class="rounded-full bg-gray-200 dark:bg-gray-700 w-8 h-8 flex items-center justify-center
            {{if index 'ml-[-20px]'}}"
        >
          <Avatar
            class="font-semibold text-xs"
            @firstName={{firstName appearance.name}}
            @lastName={{lastName appearance.name}}
            @size="sm"
            @classes={{hash base=(concat "text-white " (getAvatarColor appearance.name))}}
          />
        </div>
      {{/each}}
    </div>
  </template>
}
